import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  Divider,
  CircularProgress,
  Link,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AttachFile as AttachIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { usePersonNotes } from '../../hooks/queries/usePersonDetail';

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB (alinhado ao limite por arquivo do backend)

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Dialog de criação/edição de nota com anexos.
 * Anexos só ficam disponíveis depois que a nota existe (precisa de noteId).
 * Ao criar, salva primeiro e transiciona para edição para liberar os anexos.
 */
export default function PersonNoteDialog({ open, onClose, personId, noteId = null, labelSingular = 'Anotação' }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef(null);

  const {
    notes,
    createAsync, isCreating,
    updateAsync, isUpdating,
    attachAsync, isAttaching,
    removeAttachmentAsync,
  } = usePersonNotes(personId);

  const [currentNoteId, setCurrentNoteId] = useState(noteId);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState(null);

  const currentNote = useMemo(
    () => (currentNoteId ? notes.find((n) => n.id === currentNoteId) || null : null),
    [notes, currentNoteId],
  );

  // Inicializa campos quando o dialog abre / a nota carrega.
  useEffect(() => {
    if (!open) return;
    setCurrentNoteId(noteId);
    setError(null);
  }, [open, noteId]);

  useEffect(() => {
    if (currentNote) {
      setTitle(currentNote.title || '');
      setBody(currentNote.body || '');
    } else if (!currentNoteId) {
      setTitle('');
      setBody('');
    }
  }, [currentNote, currentNoteId]);

  const attachments = currentNote?.attachments || [];
  const isSaving = isCreating || isUpdating;
  const canAttach = !!currentNoteId;

  const handleSave = async () => {
    setError(null);
    if (!title.trim()) {
      setError('Informe um título.');
      return;
    }
    try {
      if (currentNoteId) {
        await updateAsync(currentNoteId, { title: title.trim(), body, kind: 0 });
      } else {
        const created = await createAsync({ title: title.trim(), body, kind: 0 });
        setCurrentNoteId(created.id); // libera anexos
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Erro ao salvar.');
    }
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // permite re-selecionar o mesmo arquivo
    setError(null);
    for (const file of files) {
      if (file.size > MAX_FILE_BYTES) {
        setError(`"${file.name}" excede o limite de 10 MB.`);
        continue;
      }
      try {
        await attachAsync(currentNoteId, file);
      } catch (err) {
        setError(err.response?.data?.message || err.message || `Falha ao anexar "${file.name}".`);
      }
    }
  };

  const handleRemoveAttachment = async (attachmentId) => {
    setError(null);
    try {
      await removeAttachmentAsync(currentNoteId, attachmentId);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Falha ao remover anexo.');
    }
  };

  const isImage = (ct) => typeof ct === 'string' && ct.startsWith('image/');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>{currentNoteId ? `Editar ${labelSingular}` : `Nova ${labelSingular}`}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <TextField
          label="Título"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          inputProps={{ maxLength: 200 }}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Conteúdo"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          fullWidth
          multiline
          minRows={4}
          maxRows={12}
          inputProps={{ maxLength: 20000 }}
        />

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold">Anexos</Typography>
          <Button
            size="small"
            startIcon={isAttaching ? <CircularProgress size={14} /> : <AttachIcon />}
            disabled={!canAttach || isAttaching}
            onClick={() => fileInputRef.current?.click()}
          >
            Anexar
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept="application/pdf,image/png,image/jpeg,image/webp"
            onChange={handleFiles}
          />
        </Box>

        {!canAttach && (
          <Typography variant="caption" color="text.secondary">
            Salve a {labelSingular.toLowerCase()} para anexar arquivos (PDF ou imagem, até 10 MB).
          </Typography>
        )}

        {canAttach && attachments.length === 0 && (
          <Typography variant="caption" color="text.secondary">Nenhum anexo.</Typography>
        )}

        {attachments.map((att) => (
          <Box
            key={att.id}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            {isImage(att.contentType) ? (
              <Box
                component="img"
                src={att.url}
                alt={att.fileName}
                sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
              />
            ) : (
              <PdfIcon color="error" sx={{ width: 40 }} />
            )}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap>{att.fileName}</Typography>
              <Typography variant="caption" color="text.secondary">{formatBytes(att.byteSize)}</Typography>
            </Box>
            <IconButton size="small" component={Link} href={att.url} target="_blank" rel="noopener" title="Abrir">
              <OpenIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => handleRemoveAttachment(att.id)} title="Remover">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Salvando...' : (currentNoteId ? 'Salvar alterações' : 'Salvar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
